import React, { useState, useEffect } from 'react';
import { safeTrim } from '../utils/validation';
import { Plus, Edit2, Trash2, Check, X, Zap, Clock, DollarSign, Package } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HotspotPackages = () => {
  const { token, user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check if user is owner (read-only mode)
  const isOwnerRole = user?.role?.toLowerCase() === 'owner';

  const [formData, setFormData] = useState({
    name: '',
    package_type: 'hotspot', // Always hotspot for this page
    validity_period: '', // Duration (e.g., 1, 7, 30)
    validity_unit: 'days', // 'days', 'hours', 'months'
    download_speed: 5,
    upload_speed: 5,
    price: 0,
    quota_gb: null,
    description: '',
    is_active: true
  });

  useEffect(() => {
    if (token) {
      fetchPackages();
    }
  }, [token]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/service-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // Filter hanya paket hotspot - use package_type field
        const hotspotPackages = response.data.data.filter(p => 
          p.package_type === 'hotspot'
        );
        setPackages(hotspotPackages);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Gagal memuat paket hotspot');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      package_type: 'hotspot',
      validity_period: '',
      validity_unit: 'days',
      download_speed: 5,
      upload_speed: 5,
      price: 0,
      quota_gb: null,
      description: '',
      is_active: true
    });
    setEditingPackage(null);
  };

  const handleOpenModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        package_type: 'hotspot',
        validity_period: pkg.validity_period || '',
        validity_unit: pkg.validity_unit || 'days',
        download_speed: pkg.download_speed,
        upload_speed: pkg.upload_speed,
        price: pkg.price,
        quota_gb: pkg.quota_gb,
        description: pkg.description || '',
        is_active: pkg.is_active
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    // Validation
    if (!safeTrim(formData.name)) {
      toast.error('Nama paket wajib diisi');
      return;
    }

    if (!formData.name.toUpperCase().includes('HOTSPOT')) {
      toast.error('Nama paket harus mengandung kata "HOTSPOT"');
      return;
    }

    if (formData.download_speed <= 0 || formData.upload_speed <= 0) {
      toast.error('Kecepatan harus lebih dari 0');
      return;
    }

    if (formData.price < 0) {
      toast.error('Harga tidak boleh negatif');
      return;
    }

    setLoading(true);
    try {
      // Map frontend fields to backend fields
      const submitData = {
        ...formData,
        speed_down: `${formData.download_speed}M`,
        speed_up: `${formData.upload_speed}M`
      };
      
      if (editingPackage) {
        // Update existing package
        const response = await axios.put(
          `${API_URL}/api/v2/service-plans/${editingPackage.id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Paket hotspot berhasil diupdate');
          fetchPackages();
          handleCloseModal();
        }
      } else {
        // Create new package
        const response = await axios.post(
          `${API_URL}/api/v2/service-plans`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          toast.success('Paket hotspot berhasil dibuat');
          fetchPackages();
          handleCloseModal();
        }
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan paket');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (packageId, packageName) => {
    if (!window.confirm(`Yakin ingin menghapus paket "${packageName}"?`)) return;

    try {
      const response = await axios.delete(
        `${API_URL}/api/v2/service-plans/${packageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Paket hotspot berhasil dihapus');
        fetchPackages();
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus paket');
    }
  };

  const handleToggleActive = async (pkg) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/v2/service-plans/${pkg.id}`,
        { ...pkg, is_active: !pkg.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Paket ${!pkg.is_active ? 'diaktifkan' : 'dinonaktifkan'}`);
        fetchPackages();
      }
    } catch (error) {
      console.error('Error toggling package:', error);
      toast.error('Gagal mengubah status paket');
    }
  };

  const filteredPackages = packages.filter(pkg =>
    pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 lg:p-6">
      <div className="mb-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100">Paket Hotspot</h1>
        <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">Kelola paket hotspot untuk generate voucher</p>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs font-medium text-indigo-600 uppercase tracking-wide mb-1 dark:text-indigo-400">Total Paket</div>
          <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{packages.length}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1 dark:text-green-400">✓ Aktif</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {packages.filter(p => p.is_active).length}
          </div>
        </div>
        <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-4 shadow-sm dark:border-slate-700">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 dark:text-slate-400">⏸ Nonaktif</div>
          <div className="text-2xl font-bold text-slate-500 dark:text-slate-400">
            {packages.filter(p => !p.is_active).length}
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <Card className="mb-4 shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
            <Input
              type="text"
              placeholder="🔍 Cari paket..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-xs h-9 text-sm"
            />

            {/* Hide add button for owner role */}
            {!isOwnerRole && (
            <Button 
              onClick={() => handleOpenModal()}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white h-9 shadow-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Buat Paket Baru
            </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPackages.map((pkg) => (
          <Card key={pkg.id} className={`shadow-sm hover:shadow-md transition-shadow ${!pkg.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">{pkg.name}</h3>
                  {pkg.description && (
                    <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">{pkg.description}</p>
                  )}
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${pkg.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {pkg.is_active ? 'Aktif' : 'Nonaktif'}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-slate-600 dark:text-slate-300">Download:</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">{pkg.download_speed} Mbps</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-slate-600 dark:text-slate-300">Upload:</span>
                  <span className="font-semibold text-slate-900 dark:text-slate-100">{pkg.upload_speed} Mbps</span>
                </div>
                {pkg.quota_gb && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span className="text-slate-600 dark:text-slate-300">Kuota:</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{pkg.quota_gb} GB</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-slate-600 dark:text-slate-300">Harga:</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">Rp {pkg.price.toLocaleString('id-ID')}</span>
                </div>
                {/* Voucher Stock Badge */}
                <div className="flex items-center gap-2 pt-1 mt-1 border-t border-slate-100 dark:border-slate-700">
                  <Package className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-slate-600 text-sm dark:text-slate-300">Stok Voucher:</span>
                  {(pkg.voucher_stock_available ?? 0) > 0 ? (
                    <span className="font-semibold text-purple-700 text-sm dark:text-purple-300" data-testid={`pkg-stock-${pkg.id}`}>
                      {(pkg.voucher_stock_available || 0).toLocaleString('id-ID')} tersedia
                    </span>
                  ) : (
                    <span className="font-semibold text-red-600 text-sm dark:text-red-400" data-testid={`pkg-stock-${pkg.id}`}>0 tersedia</span>
                  )}
                  {(pkg.voucher_stock_used || 0) > 0 && (
                    <span className="text-[10px] text-slate-400 ml-auto dark:text-slate-500">{(pkg.voucher_stock_used || 0).toLocaleString('id-ID')} terpakai</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t">
                <Button
                  onClick={() => handleToggleActive(pkg)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  disabled={isOwnerRole}
                >
                  {pkg.is_active ? <X className="w-3 h-3 mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                  {pkg.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                </Button>
                {!isOwnerRole && (
                <>
                <Button
                  onClick={() => handleOpenModal(pkg)}
                  variant="outline"
                  size="sm"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button
                  onClick={() => handleDelete(pkg.id, pkg.name)}
                  variant="outline"
                  size="sm"
                  className="border-red-600 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
                </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPackages.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500 mb-4 dark:text-slate-400">
            {searchTerm ? 'Tidak ada paket yang sesuai pencarian' : 'Belum ada paket hotspot'}
          </p>
          {!searchTerm && !isOwnerRole && (
            <Button onClick={() => handleOpenModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Buat Paket Pertama
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Paket Hotspot' : 'Buat Paket Hotspot Baru'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Paket *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Contoh: HOTSPOT 1 JAM"
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Harus mengandung kata "HOTSPOT"</p>
            </div>

            {/* Duration/Validity Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Durasi/Masa Aktif *</Label>
                <Input
                  type="number"
                  value={formData.validity_period}
                  onChange={(e) => setFormData({...formData, validity_period: parseInt(e.target.value) || ''})}
                  placeholder="1"
                  min="1"
                  required
                />
              </div>
              <div>
                <Label>Unit Durasi *</Label>
                <select
                  className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  value={formData.validity_unit}
                  onChange={(e) => setFormData({...formData, validity_unit: e.target.value})}
                >
                  <option value="hours">Jam</option>
                  <option value="days">Hari</option>
                  <option value="months">Bulan</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Download Speed (Mbps) *</Label>
                <Input
                  type="number"
                  value={formData.download_speed}
                  onChange={(e) => setFormData({...formData, download_speed: parseInt(e.target.value)})}
                  min="1"
                  max="1000"
                />
              </div>
              <div>
                <Label>Upload Speed (Mbps) *</Label>
                <Input
                  type="number"
                  value={formData.upload_speed}
                  onChange={(e) => setFormData({...formData, upload_speed: parseInt(e.target.value)})}
                  min="1"
                  max="1000"
                />
              </div>
            </div>

            <div>
              <Label>Harga (Rp) *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                min="0"
                step="1000"
              />
            </div>

            <div>
              <Label>Kuota (GB)</Label>
              <Input
                type="number"
                value={formData.quota_gb || ''}
                onChange={(e) => setFormData({...formData, quota_gb: e.target.value ? parseInt(e.target.value) : null})}
                placeholder="Kosongkan untuk unlimited"
                min="0"
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Opsional - Kosongkan untuk unlimited data</p>
            </div>

            <div>
              <Label>Deskripsi</Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Deskripsi singkat paket"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Aktifkan paket</Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600"
              >
                {loading ? 'Menyimpan...' : (editingPackage ? 'Update' : 'Buat Paket')}
              </Button>
              <Button 
                onClick={handleCloseModal}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotspotPackages;
